import { useMutation } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { MESSAGES } from "@/modules/shared/constants/messages";
import { useToast } from "@/modules/shared/hooks/use-toast";
import type { MutationRequest } from "@/modules/shared/types/mutation-request.type";
import type { ResponseAPI } from "@/modules/shared/types/response-api.type";

import {
	type IOrderFormDeleteService,
	orderFormDeleteService,
} from "../services/order-form-delete.service";
import { useLogger } from "@/modules/shared/hooks/use-logger.hook";

export const useDeleteOrderMutation = ({
	callback,
	resolveRequest,
}: MutationRequest) => {
	const { toast } = useToast();
	const { log } = useLogger();
	const { mutateAsync: deleteOrder, isSuccess: successDeleteOrder, isPending: isLoadingDeleteOrder } =
		useMutation({
			mutationFn: (data: IOrderFormDeleteService) =>
				orderFormDeleteService(data),
			onSuccess: (data, variables) => {
				if (data?.status && data.status >= 200 && data.status < 300) {
					log("success", "order-deletion", `Pedido #${variables.orderId} deletado com sucesso`, {
						response: data,
						payload: variables,
					});
				} else {
					log("error", "order-deletion", `Erro ao deletar pedido #${variables.orderId}`, {
						response: data,
						payload: variables,
					});
				}
				resolveRequest({
					status: data.status,
					showToastInfo: true,
					message: data.message,
					showToastSuccess: true,
					callback,
				});
			},
			onError: (error: AxiosError<ResponseAPI>, variables) => {
				log("error", "order-deletion", `Erro ao deletar pedido #${variables.orderId}`, {
					error,
					payload: variables,
				});
				if (
					error.response?.data?.status > 400 &&
					error.response?.data?.status < 500
				) {
					if (error.response?.data?.message) {
						toast.info({
							title: error.response.data.message,
						});
					} else {
						toast.error({
							title: MESSAGES.ERROR_TITLE,
							description: MESSAGES.ERROR_DESCRIPTION,
							action: {
								label: MESSAGES.RELOAD,
								onClick: () => {
									window.location.reload();
								},
							},
						});
					}
				} else if (
					error.response?.data?.status > 400 &&
					error.response?.data?.status < 500 &&
					error.response?.data?.message
				) {
					toast.info({
						title: error.response.data.message,
					});
				} else {
					toast.error({
						title: MESSAGES.ERROR_TITLE,
						description: MESSAGES.ERROR_DESCRIPTION,
						action: {
							label: MESSAGES.RELOAD,
							onClick: () => {
								window.location.reload();
							},
						},
					});
				}
			},
		});

	return {
		deleteOrder,
		successDeleteOrder,
		isLoadingDeleteOrder,
	};
};
